<template>
  <div v-if="showInstallPrompt" class="pwa-install-prompt">
    <div class="prompt-content">
      <div class="prompt-icon">
        <img src="/icons/icon-96x96.svg" alt="CFB Scores" />
      </div>
      <div class="prompt-text">
        <h3>Install CFB Scores</h3>
        <p>Install our app for faster access and offline support</p>
      </div>
      <div class="prompt-actions">
        <button @click="installApp" class="install-btn">
          Install
        </button>
        <button @click="dismissPrompt" class="dismiss-btn">
          Not now
        </button>
      </div>
      <button @click="dismissPrompt" class="close-btn">
        <X :size="20" />
      </button>
    </div>
  </div>
</template>

<script>
import { X } from 'lucide-vue-next';

export default {
  name: 'PWAInstallPrompt',
  components: {
    X
  },
  data() {
    return {
      deferredPrompt: null,
      showInstallPrompt: false,
      iOSInstallPrompt: false,
      dismissedAt: null
    };
  },
  mounted() {
    this.setupInstallPrompt();
    this.checkInstallPromptNeeded();
  },
  methods: {
    setupInstallPrompt() {
      // Listen for beforeinstallprompt event
      window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        this.deferredPrompt = e;
        this.checkInstallPromptNeeded();
      });

      // Listen for appinstalled event
      window.addEventListener('appinstalled', () => {
        this.deferredPrompt = null;
        this.showInstallPrompt = false;
        console.log('PWA was installed');
      });
    },

    checkInstallPromptNeeded() {
      // Don't show if already dismissed within last 7 days
      if (this.dismissedAt) {
        const daysSinceDismissed = (Date.now() - this.dismissedAt) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < 7) {
          return;
        }
      }

      // Check if it's an iOS device
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      // Check if it's running in standalone mode (already installed)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         (window.navigator.standalone);

      // Show prompt if conditions are met
      if (!isStandalone && (this.deferredPrompt || isIOS)) {
        // Show after a delay to let user explore the app first
        setTimeout(() => {
          this.showInstallPrompt = true;
        }, 5000); // 5 seconds
      }
    },

    async installApp() {
      if (this.deferredPrompt) {
        this.deferredPrompt.prompt();
        const { outcome } = await this.deferredPrompt.userChoice;
        console.log(`User response to the install prompt: ${outcome}`);

        if (outcome === 'accepted') {
          console.log('User accepted the install prompt');
        }

        this.deferredPrompt = null;
        this.showInstallPrompt = false;
      } else {
        // For iOS or when no prompt is available, show instructions
        this.showIOSInstructions();
      }
    },

    showIOSInstructions() {
      this.$emit('show-ios-instructions');
      this.showInstallPrompt = false;
    },

    dismissPrompt() {
      this.showInstallPrompt = false;
      this.dismissedAt = Date.now();
      localStorage.setItem('pwa-prompt-dismissed', this.dismissedAt);
    },

    isIOSDevice() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent);
    },

    isInStandaloneMode() {
      return window.matchMedia('(display-mode: standalone)').matches ||
             (window.navigator.standalone);
    }
  }
};
</script>

<style scoped>
.pwa-install-prompt {
  position: fixed;
  bottom: 20px;
  left: 20px;
  right: 20px;
  z-index: 1000;
  background: linear-gradient(135deg, #161b22, #0d1117);
  border: 1px solid #30363d;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
}

.prompt-content {
  display: flex;
  align-items: center;
  padding: 16px;
  gap: 12px;
  position: relative;
}

.prompt-icon img {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background-color: #21262d;
  padding: 8px;
}

.prompt-text {
  flex: 1;
  min-width: 0;
}

.prompt-text h3 {
  margin: 0 0 4px 0;
  font-size: 1rem;
  font-weight: 600;
  color: #e0e0e0;
}

.prompt-text p {
  margin: 0;
  font-size: 0.875rem;
  color: #8b949e;
  line-height: 1.4;
}

.prompt-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.install-btn {
  background-color: #238636;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.install-btn:hover {
  background-color: #2ea043;
}

.dismiss-btn {
  background-color: transparent;
  color: #8b949e;
  border: 1px solid #30363d;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.dismiss-btn:hover {
  background-color: #21262d;
  color: #e0e0e0;
}

.close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: transparent;
  border: none;
  color: #8b949e;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.close-btn:hover {
  background-color: #21262d;
  color: #e0e0e0;
}

@media (max-width: 768px) {
  .pwa-install-prompt {
    bottom: 16px;
    left: 16px;
    right: 16px;
  }

  .prompt-content {
    padding: 12px;
    gap: 10px;
  }

  .prompt-icon img {
    width: 40px;
    height: 40px;
  }

  .prompt-text h3 {
    font-size: 0.9rem;
  }

  .prompt-text p {
    font-size: 0.8rem;
  }

  .prompt-actions {
    flex-direction: column;
    gap: 6px;
  }

  .install-btn,
  .dismiss-btn {
    padding: 6px 12px;
    font-size: 0.8rem;
  }
}

/* Dark theme support */
@media (prefers-color-scheme: light) {
  .pwa-install-prompt {
    background: linear-gradient(135deg, #ffffff, #f6f8fa);
    border-color: #d1d9e0;
  }

  .prompt-text h3 {
    color: #24292f;
  }

  .prompt-text p {
    color: #656d76;
  }

  .dismiss-btn {
    border-color: #d1d9e0;
    color: #656d76;
  }

  .dismiss-btn:hover,
  .close-btn:hover {
    background-color: #f3f4f6;
    color: #24292f;
  }

  .close-btn {
    color: #656d76;
  }
}
</style>